import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AccessChart = ({ className = "" }) => {
  const [timeRange, setTimeRange] = useState('24h');
  const [chartData, setChartData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const timeRanges = [
    { value: '1h', label: '1H' },
    { value: '6h', label: '6H' },
    { value: '24h', label: '24H' },
    { value: '7d', label: '7D' }
  ];

  useEffect(() => {
    const generateChartData = () => {
      setIsLoading(true);
      
      const now = new Date();
      const data = [];
      let intervals, format;

      switch (timeRange) {
        case '1h':
          intervals = 12;
          format = (date) => date?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
          break;
        case '6h':
          intervals = 24;
          format = (date) => date?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
          break;
        case '24h':
          intervals = 24;
          format = (date) => date?.toLocaleTimeString('en-US', { hour: '2-digit' });
          break;
        case '7d':
          intervals = 7;
          format = (date) => date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          break;
        default:
          intervals = 24;
          format = (date) => date?.toLocaleTimeString('en-US', { hour: '2-digit' });
      }

      for (let i = intervals - 1; i >= 0; i--) {
        const date = new Date(now);
        
        switch (timeRange) {
          case '1h':
            date?.setMinutes(date?.getMinutes() - (i * 5));
            break;
          case '6h':
            date?.setMinutes(date?.getMinutes() - (i * 15));
            break;
          case '24h':
            date?.setHours(date?.getHours() - i);
            break;
          case '7d':
            date?.setDate(date?.getDate() - i);
            break;
        }

        const baseAccess = Math.floor(Math.random() * 15) + 5;
        const anomalyChance = Math.random();
        const hasAnomaly = anomalyChance < 0.1; // 10% chance of anomaly
        
        data?.push({
          time: format(date),
          fullTime: date?.toISOString(),
          successful: baseAccess + Math.floor(Math.random() * 10),
          failed: Math.floor(Math.random() * 5) + (hasAnomaly ? 10 : 0),
          anomalies: hasAnomaly ? Math.floor(Math.random() * 3) + 1 : 0
        });
      }

      setChartData(data);
      setTimeout(() => setIsLoading(false), 500);
    };

    generateChartData();
    const interval = setInterval(generateChartData, 30000); // Update every 30 seconds

    return () => clearInterval(interval);
  }, [timeRange]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-card border border-border rounded-lg p-3 security-shadow-card">
          <p className="text-sm font-medium text-foreground mb-2">{label}</p>
          {payload?.map((entry, index) => (
            <div key={index} className="flex items-center space-x-2 text-sm">
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: entry?.color }}
              />
              <span className="text-muted-foreground">{entry?.name}:</span>
              <span className="font-medium text-foreground">{entry?.value}</span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className={`bg-card border border-border rounded-lg p-6 security-shadow-card ${className}`}>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-accent/10 rounded-lg flex items-center justify-center">
            <Icon name="Activity" size={18} className="text-accent" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Access Attempts</h3>
            <p className="text-sm text-muted-foreground">Real-time security monitoring</p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {timeRanges?.map((range) => (
            <Button
              key={range?.value}
              variant={timeRange === range?.value ? "default" : "ghost"}
              size="sm"
              onClick={() => setTimeRange(range?.value)}
              className="text-xs"
            >
              {range?.label}
            </Button>
          ))}
        </div>
      </div>
      <div className="h-64 w-full">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex items-center space-x-2 text-muted-foreground">
              <div className="w-4 h-4 border-2 border-accent border-t-transparent rounded-full animate-spin" />
              <span className="text-sm">Loading chart data...</span>
            </div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.1)" />
              <XAxis 
                dataKey="time" 
                stroke="#94A3B8"
                fontSize={12}
                tickLine={false}
                axisLine={false}
              />
              <YAxis 
                stroke="#94A3B8"
                fontSize={12}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip content={<CustomTooltip />} />
              <Line 
                type="monotone" 
                dataKey="successful" 
                stroke="#10B981" 
                strokeWidth={2}
                dot={{ fill: '#10B981', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: '#10B981', strokeWidth: 2 }}
                name="Successful"
              />
              <Line 
                type="monotone" 
                dataKey="failed" 
                stroke="#EF4444" 
                strokeWidth={2}
                dot={{ fill: '#EF4444', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: '#EF4444', strokeWidth: 2 }}
                name="Failed"
              />
              <Line 
                type="monotone" 
                dataKey="anomalies" 
                stroke="#F59E0B" 
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={{ fill: '#F59E0B', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: '#F59E0B', strokeWidth: 2 }}
                name="Anomalies"
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
      <div className="flex items-center justify-center space-x-6 mt-4 pt-4 border-t border-border">
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-success rounded-full" />
          <span className="text-sm text-muted-foreground">Successful Access</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-error rounded-full" />
          <span className="text-sm text-muted-foreground">Failed Attempts</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-warning rounded-full" />
          <span className="text-sm text-muted-foreground">Anomalies</span>
        </div>
      </div>
    </div>
  );
};

export default AccessChart;